import React, { useRef } from "react";
import FrictionlessCheckoutsSectionImage from "../../images/FrictionlessCheckoutsSection.webp";
import BankTransferWithCrossIcon from "../../images/BankTransferWithCrossIcon.webp";
import { ReactComponent as WalletWithCrossIcon } from "../../images/WalletWithCrossIcon.svg";
import { ReactComponent as CardWithCrossIcon } from "../../images/CardWithCrossIcon.svg";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

function FrictionlessCheckoutsSection() {
  const sectionRef = useRef(null);
  const textSectionRef = useRef(null);
  const mainImageRef = useRef(null);
  const bankTransferWithCrossIconRef = useRef(null);
  const walletWithCrossIconRef = useRef(null);
  const cardWithCrossIconRef = useRef(null);

  useGSAP(
    () => {
      gsap.fromTo(
        mainImageRef.current,
        {
          opacity: 0,
        },
        {
          scrollTrigger: {
            trigger: mainImageRef.current,
            start: "top 75%",
            end: "top 70%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
          opacity: 1,
          duration: 1.5,
          ease: "sine.out",
        }
      );

      gsap.fromTo(
        bankTransferWithCrossIconRef.current,
        {
          opacity: 0,
          x: -26,
          y: 150,
        },
        {
          scrollTrigger: {
            trigger: mainImageRef.current,
            start: "top 65%",
            end: "top 50%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
          opacity: 1,
          x: 0,
          y: 0,
          duration: 1.5,
          ease: "sine.out",
        }
      );

      gsap.fromTo(
        walletWithCrossIconRef.current,
        {
          opacity: 0,
          x: -215,
          y: 150,
        },
        {
          scrollTrigger: {
            trigger: mainImageRef.current,
            start: "top 65%",
            end: "top 50%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
          opacity: 1,
          x: 0,
          y: 0,
          duration: 1.5,
          ease: "sine.out",
        }
      );

      gsap.fromTo(
        cardWithCrossIconRef.current,
        {
          opacity: 0,
          x: -250,
          y: 22,
        },
        {
          scrollTrigger: {
            trigger: mainImageRef.current,
            start: "top 80%",
            end: "top 50%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
          opacity: 1,
          x: 0,
          y: 0,
          duration: 1.5,
          ease: "sine.out",
        }
      );

      gsap.fromTo(
        textSectionRef.current,
        {
          opacity: 0,
          y: 100,
        },
        {
          scrollTrigger: {
            trigger: textSectionRef.current,
            start: "top 80%",
            end: "top 75%",
            scrub: false,
            toggleActions: "play none none reverse",
          },
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "sine.out",
        }
      );
    },
    {
      scope: sectionRef.current,
    }
  );

  return (
    <section className="border-y border-black mt-20 xl:mt-28">
      <section
        ref={sectionRef}
        className="w-full 2xl:max-w-[1920px] mx-auto pt-20 sm:pt-28 xl:pt-36 pb-20 sm:pb-28 xl:pb-40 pl-10 xl:pl-[74px] pr-10 xl:pr-[140px] flex flex-col-reverse xl:flex-row gap-y-16 items-center xl:justify-between"
      >
        {/* Text */}
        <section
          className="xl:max-w-[725px] flex flex-col items-center sm:items-start"
          ref={textSectionRef}
        >
          <h2 className="text-[42px] xl:text-[52px] font-HalvarBreitExtraBold text-[#100C08] leading-[52.92px] xl:leading-[55.12px] text-center sm:text-left">
            Frictionless Checkouts, Frictionless Profits
          </h2>
          <p className="text-base xl:text-lg font-HalvarBreitRegular text-[#100C08] leading-[32px] xl:leading-[37.08px] text-center sm:text-left my-[18px]">
            Wouldn't it be amazing if your sales & conversions grew without your
            constant efforts?
            <br />
            <br />
            <span className="font-HalvarBreitBold">
              STRABL unlocks this potential.
            </span>
            <br />
            Imagine: one-click purchases, seamless user experience, and the
            freedom for your customers to pay however they want!
            <br />
            STRABL puts your entire checkout experience on autopilot, freeing
            you to focus on what matters most - growing your business.
          </p>
          <button
            className="text-lg font-gtAmericaMedium text-center uppercase leading-[21.69px] text-white bg-[#100C08] border border-black rounded w-fit pb-3 pt-3.5 px-6 "
            onClick={() => {
              window.open("https://forms.fillout.com/t/gM3KTnEim2us", "_blank");
            }}
          >
            Try strabl for free
          </button>
        </section>
        {/* Image */}
        <section className="w-[310px] sm:w-[423px] xl:w-[585px] xl:min-w-[584px] h-full relative">
          <img
            src={FrictionlessCheckoutsSectionImage}
            alt="Frictionless Checkouts Section"
            ref={mainImageRef}
            width={879}
            height={957}
            loading="lazy"
          />
          <img
            src={BankTransferWithCrossIcon}
            ref={bankTransferWithCrossIconRef}
            className="w-[60px] sm:w-[70px] xl:w-[96.28px] h-[61px] sm:h-[76px] xl:h-[106.08px] absolute right-24 sm:right-[7.5rem] xl:right-44 -top-8 sm:-top-5 xl:-top-10"
            alt="Bank Transfer"
            width={97}
            height={107}
            loading="lazy"
          />
          <WalletWithCrossIcon
            ref={walletWithCrossIconRef}
            className="w-w-[37.02px] sm:w-[50.34px] xl:w-[69.56px] h-[46.28px] sm:h-[52.99px] xl:h-[73.22px] absolute -top-3.5 sm:top-0 xl:top-0 -right-6 sm:right-0 xl:right-0"
          />
          <CardWithCrossIcon
            ref={cardWithCrossIconRef}
            className="w-[47px] sm:w-[53.97px] xl:w-[74.58px] h-[34.47px] sm:h-[42.9px] xl:h-[59.28px] absolute -right-8 sm:-right-6 xl:-right-10 top-20 sm:top-[6.5rem] xl:top-36"
          />
        </section>
      </section>
    </section>
  );
}

export default FrictionlessCheckoutsSection;
